import { Button, Col, Row, Spinner, Modal, Form, Table } from "react-bootstrap";
import Error from "../abstractComponents/error";
import { useState } from "react";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { useExportFilesMutation } from "../../backend/hooks/interactive/mutationExportFiles";
import UserManualLink from "../abstractComponents/usermanual";
import { getQualityCheckStatusKeyFromValue } from "../../helpers/stringHelper";
import {
  QualityCheckStatusUI,
  QualityCheckStatusType,
  ActionFileType
} from "../../backend/types";
import { deduplicateByKey } from "../../helpers/interactiveHelper";
import { CustomDifference } from "./interactiveFileTransfer";
import { DifferenceState } from "../../backend/interactiveTypes";

export function InteractiveFileTransferImportSimple(props: {
  environmentId: string;
  selectedRows: CustomDifference[];
  setSuccessMessage: (message: { message: string; id: number } | null) => void;
}) {
  const { exportFiles, isLoadingExportFiles, errorExportFiles } =
    useExportFilesMutation();

  const [isSimpleModalVisible, setIsSimpleModalVisible] = useState(false);
  const [description, setDescription] = useState("");
  const [qualityCheckStatus, setQualityCheckStatus] =
    useState<QualityCheckStatusUI>(QualityCheckStatusUI.NOT_STARTED);

  const filesToBeDeleted: CustomDifference[] = props.selectedRows?.filter(
    (file: CustomDifference) => file.type === DifferenceState.Deleted
  );

  const filesToBeUploaded = props.selectedRows
    ?.filter(
      (file: CustomDifference) =>
        file.type === DifferenceState.Added ||
        file.type === DifferenceState.Modified
    )
    .map((uploadFile) => ({
      ...uploadFile,
      path: uploadFile.key,
      status: ActionFileType.Output
    }));

  function changeQualityCheckedStatus(
    event: React.ChangeEvent<HTMLSelectElement>
  ) {
    event.preventDefault();
    setQualityCheckStatus(
      QualityCheckStatusUI[event.target.value as QualityCheckStatusType]
    );
  }

  const performSimpleUpload = () => {
    const exportPaths = deduplicateByKey(filesToBeUploaded, "fileName").map(
      (row) => ({
        actionFileType: row.actionFileType,
        path: row.fileName
      })
    );
    const deletePaths = Array.from(
      new Set(
        filesToBeDeleted.map((deleted: CustomDifference) => deleted.fileName)
      )
    );

    exportFiles({
      variables: {
        environmentId: props.environmentId,
        exportPaths,
        deletePaths,
        qualityCheckStatus:
          getQualityCheckStatusKeyFromValue(qualityCheckStatus),
        description: description,
        actionType: "UPLOAD"
      }
    }).then(() => {
      setIsSimpleModalVisible(false);
      props.setSuccessMessage({
        message: "Exporting files, please check the notification for progress.",
        id: Date.now()
      });
    });
  };

  return (
    <>
      <Button
        id="proced_with_import_simple"
        className="button-secondary float-start"
        variant="primary"
        onClick={() => {
          setIsSimpleModalVisible(true);
        }}
      >
        <FileUploadIcon /> Simple Upload
      </Button>{" "}
      <Modal
        show={isSimpleModalVisible}
        onHide={() => {
          setIsSimpleModalVisible(false);
        }}
        dialogClassName="modal-75w interactive_modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Simple File Upload from Interactive</Modal.Title>
          <UserManualLink
            url="/usermanual/datatransfer/#simple-upload"
            testId="um-ie-simple-upload"
            className="align-self-start ms-1"
          />
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col xs={3}>
              <Form.Label>Action Type</Form.Label>
              <Form.Select disabled>
                <option>Upload</option>
              </Form.Select>
            </Col>
            <Col xs={3}></Col>
            <Col xs={6}>
              <Form.Group className="mb-3" controlId="upload_description">
                <Form.Label>
                  Upload Description <span className="red_form">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  required
                  placeholder="Please enter a description"
                  value={description}
                  disabled={isLoadingExportFiles}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col xs={3}>
              <Form.Label className="mt-3">Quality Control Status</Form.Label>
            </Col>
            <Col xs={3}></Col>
            <Col xs={6}>
              <Form.Select
                value={String(
                  getQualityCheckStatusKeyFromValue(qualityCheckStatus)
                )}
                onChange={changeQualityCheckedStatus}
                key="ie_external_action_quality_controlled"
                id="externalActionIEQualityControlled"
              >
                {Object.entries(QualityCheckStatusUI).map((element) => (
                  <option key={element[0]} value={element[0]}>
                    {element[1]}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>

          {isLoadingExportFiles && (
            <Row className="loading_row">
              <Col className="text-center">
                <Spinner animation="border" className="spinner_color" />
                <br />
                Loading...
              </Col>
            </Row>
          )}
          <br />
          <br />
          <Button
            className="d-flex ml-auto mb-2 ms-auto"
            variant="primary"
            disabled={description === "" || isLoadingExportFiles}
            onClick={performSimpleUpload}
            id="perform_simple_upload_button"
          >
            Perform Actions
          </Button>
          <h3 className="font-weight-normal">Preview - Resulting Actions:</h3>
          <div className="overflow-auto interactive-row">
            <Table
              striped
              bordered
              hover
              size="sm"
              className="resulting_action_preview"
            >
              <thead>
                <tr>
                  <th>Action Type</th>
                  <th>Files</th>
                </tr>
              </thead>
              <tbody>
                {filesToBeDeleted.map((file: CustomDifference, index) => (
                  <tr key={`deleted_${index}`}>
                    <td valign="middle">Hide</td>
                    <td className="resulting_action_preview_div">{file.key}</td>
                  </tr>
                ))}
                {filesToBeUploaded.map((file: CustomDifference, index) => (
                  <tr key={`uploaded_${index}`}>
                    <td valign="middle">Upload</td>
                    <td className="resulting_action_preview_div">{file.key}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setIsSimpleModalVisible(false);
            }}
            disabled={isLoadingExportFiles}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      {errorExportFiles && <Error error={errorExportFiles} />}
    </>
  );
}
